import { useState } from "react";
import { X, ExternalLink, Check, Plus, Minus, ArrowRight } from "lucide-react";
import { asset } from "@/lib/api";
import { PinMedia } from "@/threedvista/TdvExtras";

export const LAYOUTS = [["classic", "Classic card"], ["tabs", "Tabs"], ["split", "Split"], ["list", "Expandable list"], ["numbers", "In numbers"], ["visit", "Visitor info + Book a visit"], ["banner", "Banner"]];

const themeOf = (light) => ({
  panel: light ? "bg-white text-slate-900 shadow-2xl" : "glass-dark text-white",
  muted: light ? "text-slate-600" : "text-white/75",
  chip: light ? "bg-slate-100" : "bg-white/10",
  line: light ? "border-slate-200" : "border-white/15",
  hover: light ? "hover:bg-slate-100" : "hover:bg-white/10",
});

const Close = ({ onClose, th }) => <button onClick={onClose} className={`absolute right-3 top-3 z-10 w-8 h-8 rounded-full ${th.hover} grid place-items-center`} data-testid="tdv-pin-card-close"><X className="w-4 h-4" /></button>;

function Cta({ pin, w, onBook, th }) {
  const label = w.cta_label || (w.layout === "visit" ? "Book a visit" : pin.link_url ? "Learn more" : "");
  if (!label) return null;
  const cls = "h-11 px-5 rounded-full bg-bp text-white text-sm font-semibold inline-flex items-center gap-2 shrink-0 hover:brightness-110 transition-[filter]";
  if (pin.link_url && w.layout !== "visit") return <a href={pin.link_url} target="_blank" rel="noreferrer" className={cls} data-testid="tdv-win-cta">{label}<ExternalLink className="w-4 h-4" /></a>;
  return <button onClick={onBook} className={cls} data-testid="tdv-win-cta">{label}<ArrowRight className="w-4 h-4" /></button>;
}

function Head({ pin, th }) {
  return (
    <>
      <p className="font-serif text-2xl sm:text-3xl pr-8 leading-tight" data-testid="tdv-pin-card-title">{pin.title || "Details"}</p>
      {pin.body && <p className={`text-sm ${th.muted} mt-2 whitespace-pre-line`}>{pin.body}</p>}
    </>
  );
}

function Tabs({ items, th }) {
  const [i, setI] = useState(0);
  const cur = items[Math.min(i, items.length - 1)];
  return (
    <div className="mt-5">
      <div className={`flex gap-1 overflow-x-auto border-b ${th.line}`}>{items.map((it, j) => (
        <button key={j} onClick={() => setI(j)} className={`px-4 h-10 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${j === i ? "border-[var(--bp,#0F172A)] text-bp" : `border-transparent ${th.muted}`}`} data-testid={`tdv-win-tab-${j}`}>{it.title}</button>
      ))}</div>
      {cur && <p className={`text-sm ${th.muted} mt-4 whitespace-pre-line`} data-testid="tdv-win-tab-text">{cur.text}</p>}
    </div>
  );
}

function Accordion({ items, th }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="mt-5 space-y-2">{items.map((it, j) => (
      <div key={j} className={`rounded-2xl ${th.chip} px-4`} data-testid={`tdv-win-item-${j}`}>
        <button onClick={() => setOpen(open === j ? -1 : j)} className="w-full h-12 flex items-center justify-between text-sm font-semibold text-left">{it.title}{open === j ? <Minus className="w-4 h-4 shrink-0" /> : <Plus className="w-4 h-4 shrink-0" />}</button>
        {open === j && it.text && <p className={`text-sm ${th.muted} pb-4 -mt-1 whitespace-pre-line`}>{it.text}</p>}
      </div>
    ))}</div>
  );
}

function Body({ pin, w, items, th, onBook }) {
  const img = pin.image_url && <img src={asset(pin.image_url)} alt="" className="w-full h-full min-h-48 object-cover rounded-2xl" />;
  const foot = <><PinMedia pin={pin} light={w.theme === "light"} /><div className="mt-5"><Cta pin={pin} w={w} onBook={onBook} th={th} /></div></>;
  if (w.layout === "split") return (
    <div className="grid md:grid-cols-2 gap-6">
      {img || <div className={`rounded-2xl ${th.chip} min-h-48`} />}
      <div><Head pin={pin} th={th} />
        <ul className="mt-4 space-y-2">{items.map((it, j) => <li key={j} className="flex gap-3 text-sm" data-testid={`tdv-win-item-${j}`}><span className="w-5 h-5 rounded-full bg-bp text-white grid place-items-center shrink-0"><Check className="w-3 h-3" /></span><span><b>{it.title}</b>{it.text && <span className={th.muted}> · {it.text}</span>}</span></li>)}</ul>
        {foot}</div>
    </div>
  );
  if (w.layout === "list") return (
    <div className={`grid gap-6 ${img ? "md:grid-cols-[1fr_15rem]" : ""}`}>
      <div><Head pin={pin} th={th} /><Accordion items={items} th={th} />{foot}</div>{img && <div className="hidden md:block">{img}</div>}
    </div>
  );
  if (w.layout === "numbers") return (
    <div>{pin.image_url && <img src={asset(pin.image_url)} alt="" className="w-full h-40 object-cover rounded-2xl mb-5" />}<Head pin={pin} th={th} />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">{items.map((it, j) => (
        <div key={j} className={`rounded-2xl ${th.chip} p-4`} data-testid={`tdv-win-item-${j}`}><p className="font-serif text-3xl sm:text-4xl text-bp leading-none">{it.value || "—"}</p><p className={`text-xs ${th.muted} mt-2 uppercase tracking-wider`}>{it.title}</p></div>
      ))}</div>{foot}</div>
  );
  if (w.layout === "visit") return (
    <div className={`grid gap-6 ${img ? "md:grid-cols-[1fr_16rem]" : ""}`}>
      <div><Head pin={pin} th={th} />
        <dl className={`mt-5 divide-y ${th.line} border-y ${th.line}`}>{items.map((it, j) => <div key={j} className={`py-3 grid grid-cols-[7rem_1fr] gap-3 text-sm divide-none ${th.line}`} data-testid={`tdv-win-item-${j}`}><dt className="font-semibold">{it.title}</dt><dd className={`${th.muted} whitespace-pre-line`}>{it.text}</dd></div>)}</dl>
        {foot}</div>{img && <div className="hidden md:block">{img}</div>}
    </div>
  );
  return (
    <div>{pin.image_url && <img src={asset(pin.image_url)} alt="" className="w-full h-44 object-cover rounded-2xl mb-5" />}<Head pin={pin} th={th} /><Tabs items={items} th={th} />{foot}</div>
  );
}

export function InfoWindow({ pin, onClose, onBook }) {
  if (!pin) return null;
  const w = pin.win || {};
  const th = themeOf(w.theme === "light");
  const items = (w.items || []).filter((i) => i.title || i.value || i.text);
  const layout = w.layout || "classic";
  if (layout === "classic") return (
    <div className={`absolute z-30 left-3 bottom-3 w-[min(22rem,calc(100%-1.5rem))] max-h-[calc(100%-6rem)] overflow-y-auto rounded-3xl ${th.panel} p-5 rise`} data-testid="tdv-pin-card">
      <Close onClose={onClose} th={th} />
      {pin.image_url && <img src={asset(pin.image_url)} alt="" className="w-full h-36 object-cover rounded-2xl mb-3" />}
      <p className="font-serif text-xl pr-8" data-testid="tdv-pin-card-title">{pin.title || "Details"}</p>
      {pin.body && <p className={`text-sm ${th.muted} mt-2 whitespace-pre-line`}>{pin.body}</p>}
      {pin.link_url && <a href={pin.link_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold underline" data-testid="tdv-pin-card-link">{w.cta_label || "Learn more"} <ExternalLink className="w-3.5 h-3.5" /></a>}
      <PinMedia pin={pin} light={w.theme === "light"} />
    </div>
  );
  if (layout === "banner") return (
    <div className={`absolute z-30 bottom-20 left-3 right-3 md:bottom-3 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-[min(52rem,calc(100%-26rem))] rounded-3xl ${th.panel} p-4 sm:p-5 rise flex flex-col sm:flex-row sm:items-center gap-4`} data-testid="tdv-pin-card">
      <Close onClose={onClose} th={th} />
      {pin.image_url && <img src={asset(pin.image_url)} alt="" className="hidden sm:block w-28 h-20 object-cover rounded-2xl shrink-0" />}
      <div className="flex-1 min-w-0 pr-8"><p className="font-serif text-xl sm:text-2xl leading-tight" data-testid="tdv-pin-card-title">{pin.title || "Details"}</p>{pin.body && <p className={`text-sm ${th.muted} mt-1 line-clamp-2`}>{pin.body}</p>}</div>
      <Cta pin={pin} w={{ ...w, cta_label: w.cta_label || (pin.link_url ? "Learn more" : "Get in touch") }} onBook={onBook} th={th} />
    </div>
  );
  return (
    <div className="absolute inset-0 z-40 grid place-items-center bg-black/40 p-3 sm:p-6" onClick={onClose} data-testid="tdv-win-backdrop">
      <div className={`relative w-full max-w-3xl max-h-full overflow-y-auto rounded-3xl ${th.panel} p-5 sm:p-8 rise`} onClick={(e) => e.stopPropagation()} data-testid="tdv-pin-card" data-layout={layout}>
        <Close onClose={onClose} th={th} />
        <Body pin={pin} w={{ ...w, layout }} items={items} th={th} onBook={onBook} />
      </div>
    </div>
  );
}
